import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Grid, IconButton, Paper, Slider, Tooltip, Typography } from '@mui/material';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import PauseOutlinedIcon from '@mui/icons-material/PauseOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import SkipNextOutlinedIcon from '@mui/icons-material/SkipNextOutlined';
import SkipPreviousOutlinedIcon from '@mui/icons-material/SkipPreviousOutlined';
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import { brand, neutral } from '../../theme';
import { DEFAULT_FPS, formatTimecode } from '../../utils/timecode';
import {
  ReviewAngle,
  ReviewAnnotation,
  angleCovers,
  angleTimelineRange,
  annotationColor,
  annotationsNear,
  hasKnownLength,
  isSwitch,
  liveAngleAt,
  nextStopMs,
  previousStopMs,
} from './multicamReview';
import { angleName, cutLabel } from './multicamStudioModel';
import { angleColor, playerTokens } from './multicamTheme';
import { recordingPhrase } from './separateAudio';
import { useSyncedAngles } from './useSyncedAngles';

interface MulticamReviewPlayerProps {
  angles: ReviewAngle[];
  audioAngleKey: string;
  /** The set's separate sound recording: heard instead of the audio angle. */
  audioSource?: ReviewAngle | null;
  timelineDurationMs?: number | null;
  annotations: ReviewAnnotation[];
  /** Change `nonce` to seek from outside, e.g. when a note is clicked in a list. */
  seekRequest?: { timeMs: number; nonce: number } | null;
  /** Reports the shared clock (throttled), e.g. to drive a timeline playhead. */
  onTimeChange?: (timeMs: number, playing: boolean) => void;
  /** Frame rate for timecodes (HH:MM:SS:FF); 30 when not known. */
  fps?: number;
  /**
   * `panel` (default) draws its own outlined panel; `plain` drops the frame
   * for use inside a card that already has one.
   */
  variant?: 'panel' | 'plain';
}

const SPEEDS = [0.5, 1, 1.5, 2];
const TIMECODE_FONT = '"JetBrains Mono", "SFMono-Regular", Menlo, Consolas, monospace';

/** A small label laid over the picture. */
const overlayLabelSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  px: 1,
  py: 0.25,
  borderRadius: 1.5,
  bgcolor: playerTokens.overlay,
  color: playerTokens.text,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.6,
} as const;

/**
 * Every angle of a multicam project on one shared clock, with the creator's
 * notes and camera cuts on the scrubber. Editors used to get only a list of
 * notes and a download button per angle, so checking a cut meant lining the
 * files up by hand in an NLE.
 *
 * Playback and sync (MC-31) live in `useSyncedAngles`, shared with the
 * studio's live cut multiview.
 */
const MulticamReviewPlayer: React.FC<MulticamReviewPlayerProps> = ({
  angles,
  audioAngleKey,
  audioSource,
  timelineDurationMs,
  annotations,
  seekRequest,
  onTimeChange,
  fps = DEFAULT_FPS,
  variant = 'panel',
}) => {
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const {
    sortedAngles,
    lengthMs,
    soundKeyAt,
    currentMs,
    playing,
    tiles,
    videoRefs,
    setTile,
    play,
    pause,
    seek,
    handleLoadedMetadata,
    retryAngle,
    audioSource: recording,
    audioSourceRef,
    handleAudioSourceLoadedMetadata,
  } = useSyncedAngles<ReviewAngle>({ angles, audioAngleKey, audioSource, timelineDurationMs, muted, speed, onTimeChange });

  // Outside seek requests (a note clicked in the list).
  useEffect(() => {
    if (seekRequest) seek(seekRequest.timeMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seekRequest?.nonce]);

  const timecode = (ms: number) => formatTimecode(ms / 1000, fps);
  const describe = (annotation: ReviewAnnotation) =>
    isSwitch(annotation) ? cutLabel(annotation.targetAngleKey, sortedAngles) : annotation.text || 'Note';

  const liveKey = liveAngleAt(annotations, currentMs, 'A');
  // The angle heard now: the audio angle where it has footage, else the clip
  // that has (clips played one after another); none while a recording is.
  const heardKey = soundKeyAt(currentMs);
  const nearby = annotationsNear(annotations, currentMs);
  const percent = (ms: number) => (lengthMs > 0 ? Math.min(100, Math.max(0, (ms / lengthMs) * 100)) : 0);
  // Four angles make a 2×2 grid; three, five or six go three across.
  const tileWidth = sortedAngles.length === 4 ? 6 : sortedAngles.length >= 3 ? 4 : sortedAngles.length === 2 ? 6 : 12;
  const previous = previousStopMs(annotations, currentMs);
  const next = nextStopMs(annotations, currentMs);
  const plain = variant === 'plain';

  return (
    <Paper
      variant="outlined"
      sx={plain ? { p: 0, border: 0, bgcolor: 'transparent', borderRadius: 0 } : { p: { xs: 1.5, sm: 2 }, borderRadius: 3 }}
      data-testid="multicam-review-player"
    >
      <Typography variant="h6" component="h2" sx={{ fontWeight: 600, fontSize: 16, color: 'text.primary' }}>
        Review player
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
        Every angle plays in sync. Markers on the scrubber are the creator&apos;s camera cuts and notes; the angle
        marked Live is the one their cut shows at that point.{' '}
        {recording
          ? `You hear ${recordingPhrase(recording)}. Where it doesn’t cover, you hear the angle marked Audio.`
          : 'The angle marked Audio is the one you hear.'}
      </Typography>
      {recording && (
        // The set's own recorder: heard and keeping time, never shown.
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio
          ref={audioSourceRef}
          src={recording.videoUrl}
          preload="auto"
          data-testid="review-audio-source"
          onLoadedMetadata={(event) => handleAudioSourceLoadedMetadata(event.currentTarget)}
          style={{ display: 'none' }}
        />
      )}

      <Grid container spacing={1.5}>
        {sortedAngles.map((angle) => {
          const covers = angleCovers(angle, currentMs);
          const isLive = angle.angleKey === liveKey;
          const isAudio = angle.angleKey === heardKey;
          const tile = tiles[angle.angleKey] ?? 'loading';
          return (
            <Grid item xs={12} sm={sortedAngles.length > 1 ? 6 : 12} md={tileWidth} key={angle.angleKey}>
              <Box
                sx={{
                  position: 'relative',
                  aspectRatio: '16 / 9',
                  bgcolor: playerTokens.stage,
                  borderRadius: 2,
                  overflow: 'hidden',
                  outline: `3px solid ${isLive ? playerTokens.live : 'transparent'}`,
                  outlineOffset: -3,
                }}
                data-testid={`review-angle-${angle.angleKey}`}
                data-live={isLive ? 'true' : 'false'}
                data-state={tile}
              >
                <video
                  ref={(el) => { videoRefs.current[angle.angleKey] = el; }}
                  src={angle.videoUrl}
                  preload="metadata"
                  playsInline
                  onLoadedMetadata={(event) => handleLoadedMetadata(angle, event.currentTarget)}
                  onCanPlay={() => setTile(angle.angleKey, 'ready')}
                  onPlaying={() => setTile(angle.angleKey, 'ready')}
                  onSeeked={(event) => { if (event.currentTarget.readyState >= 3) setTile(angle.angleKey, 'ready'); }}
                  onWaiting={() => setTile(angle.angleKey, 'buffering')}
                  onError={() => setTile(angle.angleKey, 'error')}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                  aria-label={`Angle ${angle.angleKey}${angle.label ? `, ${angle.label}` : ''}`}
                />
                {/* Plain labels: the theme's Chip styling overrides a dark background. */}
                <Box sx={{ position: 'absolute', top: 6, left: 6, maxWidth: '60%', ...overlayLabelSx }}>
                  <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, flexShrink: 0, borderRadius: '2px', bgcolor: angleColor(angle.angleKey), boxShadow: `0 0 0 1px ${playerTokens.border}` }} />
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {angleName(angle)}
                  </Box>
                </Box>
                <Box sx={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 0.5 }}>
                  {isLive && (
                    <Box component="span" sx={{ ...overlayLabelSx, bgcolor: playerTokens.live }}>
                      Live
                    </Box>
                  )}
                  {isAudio && (
                    <Box component="span" data-testid={`review-audio-${angle.angleKey}`} sx={overlayLabelSx}>
                      <VolumeUpOutlinedIcon sx={{ fontSize: 14 }} aria-hidden="true" />
                      Audio
                    </Box>
                  )}
                </Box>
                {tile === 'error' ? (
                  <Box
                    role="alert"
                    sx={{
                      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', gap: 1,
                      alignItems: 'center', justifyContent: 'center', bgcolor: playerTokens.overlay, color: playerTokens.text, textAlign: 'center', px: 2,
                    }}
                  >
                    <ErrorOutlineOutlinedIcon fontSize="small" aria-hidden="true" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Couldn&apos;t load this angle</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => retryAngle(angle)}
                      sx={{
                        color: playerTokens.text,
                        borderColor: playerTokens.borderStrong,
                        '&:hover': { borderColor: playerTokens.text, bgcolor: playerTokens.hover },
                        '&:focus-visible': { outline: `2px solid ${playerTokens.focus}`, outlineOffset: 2 },
                      }}
                    >
                      Try again
                    </Button>
                  </Box>
                ) : !covers ? (
                  <Box sx={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: playerTokens.overlay, color: playerTokens.text, textAlign: 'center', px: 2,
                  }}>
                    <Typography variant="body2">No footage from this angle here</Typography>
                  </Box>
                ) : tile === 'buffering' && playing ? (
                  <Box
                    sx={{
                      position: 'absolute', left: 0, right: 0, bottom: 8, display: 'flex', justifyContent: 'center', pointerEvents: 'none',
                    }}
                  >
                    <Box sx={overlayLabelSx}>
                      <CircularProgress size={12} sx={{ color: playerTokens.text }} />
                      Buffering…
                    </Box>
                  </Box>
                ) : null}
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* Scrubber with note and cut markers */}
      <Box sx={{ position: 'relative', mt: 2, pt: 2.5 }}>
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 18 }}>
          {annotations.map((annotation, index) => {
            const label = describe(annotation);
            const at = timecode(annotation.timelineTimeMs);
            return (
              <Tooltip key={`${annotation.timelineTimeMs}:${index}`} title={`${at} · ${label}`}>
                <Box
                  component="button"
                  type="button"
                  aria-label={`Jump to ${at}: ${label}`}
                  onClick={() => seek(annotation.timelineTimeMs)}
                  sx={{
                    position: 'absolute',
                    left: `${percent(annotation.timelineTimeMs)}%`,
                    transform: 'translateX(-50%)',
                    top: 0,
                    width: 8,
                    height: 16,
                    p: 0,
                    border: 0,
                    borderRadius: '2px',
                    cursor: 'pointer',
                    bgcolor: annotationColor(annotation),
                    '&:hover, &:focus-visible': { height: 18, outline: `2px solid ${brand.ink}`, outlineOffset: 1 },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
        <Slider
          aria-label="Timeline position"
          value={currentMs}
          min={0}
          max={Math.max(lengthMs, 1)}
          step={1000 / Math.max(1, fps)}
          onChange={(_, value) => seek(value as number)}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => timecode(value)}
          sx={{ py: 1, '& .MuiSlider-valueLabel': { fontFamily: TIMECODE_FONT, fontVariantNumeric: 'tabular-nums' } }}
        />

        {/* Where each angle has footage */}
        {sortedAngles.length > 1 && sortedAngles.map((angle) => {
          const [start, end] = angleTimelineRange(angle);
          const known = hasKnownLength(angle);
          return (
            <Box key={angle.angleKey} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="caption" sx={{ width: 16, fontWeight: 700, color: 'text.secondary' }}>{angle.angleKey}</Typography>
              <Box sx={{ position: 'relative', flex: 1, height: 6, bgcolor: neutral[200], borderRadius: 1 }}>
                <Box sx={{
                  position: 'absolute', top: 0, bottom: 0, borderRadius: 1,
                  left: `${percent(start)}%`,
                  width: known ? `${Math.max(0.5, percent(end) - percent(start))}%` : `${100 - percent(start)}%`,
                  bgcolor: angleColor(angle.angleKey),
                  opacity: known ? 1 : 0.45,
                }} />
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Transport */}
      <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.5, mt: 1, flexWrap: 'wrap' }}>
        <Tooltip title="Previous note or cut">
          <span>
            <IconButton aria-label="Previous note or cut" onClick={() => previous !== null && seek(previous)} disabled={previous === null}>
              <SkipPreviousOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton
          aria-label={playing ? 'Pause' : 'Play'}
          onClick={playing ? pause : play}
          sx={{
            width: 40,
            height: 40,
            bgcolor: brand.ink,
            color: 'common.white',
            '&:hover': { bgcolor: brand.slate },
            '&:focus-visible': { outline: `2px solid ${brand.copper}`, outlineOffset: 2 },
          }}
        >
          {playing ? <PauseOutlinedIcon /> : <PlayArrowOutlinedIcon />}
        </IconButton>
        <Tooltip title="Next note or cut">
          <span>
            <IconButton aria-label="Next note or cut" onClick={() => next !== null && seek(next)} disabled={next === null}>
              <SkipNextOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Typography
          variant="body2"
          sx={{ fontFamily: TIMECODE_FONT, fontVariantNumeric: 'tabular-nums', fontWeight: 600, ml: 0.5 }}
          data-testid="review-clock"
        >
          {timecode(currentMs)}
          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}> / {timecode(lengthMs)}</Box>
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Playback speed">
          <Button
            size="small"
            onClick={() => setSpeed(SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length])}
            aria-label={`Playback speed ${speed}x`}
            sx={{ minWidth: 48, color: 'text.primary', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
          >
            {speed}×
          </Button>
        </Tooltip>
        <Tooltip title={muted ? 'Unmute' : 'Mute'}>
          <IconButton aria-label={muted ? 'Unmute' : 'Mute'} onClick={() => setMuted((m) => !m)} size="small">
            {muted ? <VolumeOffOutlinedIcon fontSize="small" /> : <VolumeUpOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* What the creator asked for at this moment */}
      <Box sx={{ mt: 1.5, minHeight: 24, display: 'grid', gap: 0.75 }} aria-live="polite">
        {nearby.slice(0, 3).map((annotation, index) => (
          <Box key={`${annotation.timelineTimeMs}:${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'baseline', minWidth: 0 }}>
            <Box aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: annotationColor(annotation), flexShrink: 0, alignSelf: 'center' }} />
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
              <Box component="span" sx={{ fontFamily: TIMECODE_FONT, fontVariantNumeric: 'tabular-nums', fontWeight: 600, mr: 1 }}>
                {timecode(annotation.timelineTimeMs)}
              </Box>
              {isSwitch(annotation) ? describe(annotation) : ''}
              {isSwitch(annotation) && annotation.text ? ' · ' : ''}
              {annotation.text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default MulticamReviewPlayer;
